import React, { Fragment, FC } from 'react';
import { Form, Formik, Field } from 'formik';
import FormControlBar from 'Components/WelcomeSlides/FormControlBar';
import { useMutation, useQuery } from '@apollo/client';
import { GET_COMPANY_DETAILS } from 'graphql/secure/queries';
import { useLogEvent } from 'hooks/useLogEvent';
import useMessages from 'hooks/useMessages';
import { UPDATE_COMPANY_CONFIGS } from 'graphql/secure/mutations';
import Loader from 'Components/Forms/Loader';
import { Fade, Grid } from '@mui/material';
import { PageContainer, TextContainer, ImageContainer, Headline, SkipButton } from './styles';
import ColorSelector from 'Components/Forms/ColorSelector';
import designSettingsSchema from 'FeatureModules/Setting/schemas/designSettings.schemas';
import { mergeDefaults } from 'utils/form.utils';
import useWelcomeSlides from 'hooks/useWelcomeSlides';
import { FieldWrapper, ThemeTypography } from 'DesignSystem';
import { ICompanyDesign } from 'FeatureModules/Auth/types/company.types';
import FontsAutocomplete from 'Components/Forms/FontsAutocomplete';
import DesignPreview from 'FeatureModules/Setting/Components/DesignPreview';
import ErrorState from 'Components/ErrorState';

interface IDesignProps {
  active: boolean;
}

const Design: FC<IDesignProps> = ({ active }) => {
  const { logEvent } = useLogEvent();
  const {
    data: { company } = {},
    error,
    loading,
  } = useQuery(GET_COMPANY_DETAILS, {
    fetchPolicy: 'cache-only',
  });

  const { closeDialog } = useWelcomeSlides();
  const { setSuccessMessage, setErrorMessage } = useMessages();
  const [updateCompanyConfigs] = useMutation(UPDATE_COMPANY_CONFIGS, {
    onCompleted: () => {
      setSuccessMessage('Success! Company design was updated.');
      logEvent('WELCOME_SLIDES_UPDATE_DESIGN');
      closeDialog();
    },
    onError: () => {
      setErrorMessage('Something went wrong updating your company design.');
    },
  });

  const handleSubmit = (values: ICompanyDesign) => {
    return updateCompanyConfigs({
      variables: {
        input: {
          design: values,
        },
      },
    });
  };

  if (error) return <ErrorState error={error} />;
  if (!company && loading) return <Loader />;

  const initialValues = mergeDefaults(designSettingsSchema.default(), company?.design);

  return (
    <Formik
      initialValues={initialValues}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={designSettingsSchema}
      onSubmit={values => handleSubmit(values)}
    >
      {({ isSubmitting, submitForm, values }) => (
        <Fragment>
          <PageContainer>
            <TextContainer>
              <Form>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Headline variant="TITLE_LARGE">Branding</Headline>
                    <ThemeTypography variant="BODY_LARGE">
                      Add your brand color and typefaces to style your product stories.
                    </ThemeTypography>
                  </Grid>
                  <Grid item xs={12}>
                    <FieldWrapper label="Header font">
                      <Field
                        component={FontsAutocomplete}
                        resultLimit={10}
                        fullWidth
                        name="typography.header"
                        variant="outlined"
                      />
                    </FieldWrapper>
                  </Grid>
                  <Grid item xs={12}>
                    <FieldWrapper label="Accent color">
                      <Field name="palette.primary" component={ColorSelector} />
                    </FieldWrapper>
                  </Grid>
                </Grid>
              </Form>
            </TextContainer>
            <Fade in={active} timeout={500}>
              <ImageContainer>
                <SkipButton onClick={closeDialog}>Edit this later</SkipButton>
                <DesignPreview
                  fontFamily={values.typography.header}
                  color={values.palette.primary}
                  companyName={company.name}
                  logoUrl={company.logo?.url}
                />
              </ImageContainer>
            </Fade>
          </PageContainer>
          <FormControlBar onSubmit={submitForm} isSubmitting={isSubmitting} submitButton />
        </Fragment>
      )}
    </Formik>
  );
};

export default Design;
